import React from 'react';

/**
 * ReviewCard
 *
 * Layout:
 *  Row 1 (3 columns):
 *    - Col 1: avatar image
 *    - Col 2: name + hashtag (stacked)
 *    - Col 3: 5-star rating (right-aligned)
 *  Row 2:
 *    - title (small, bold) + review text underneath
 *
 * Props:
 *  - avatarSrc: string
 *  - name: string
 *  - hashtag: string (include leading # or pass plain text; we’ll prefix # if missing)
 *  - rating: number (0..5, can be fractional like 4.5)
 *  - reviewTitle: string
 *  - reviewText: string
 *  - theme: 'dark' | 'light'
 *  - className: string (optional)
 */
export default function ReviewCard({
  avatarSrc,
  name,
  hashtag,
  rating = 5,
  reviewTitle,
  reviewText,
  theme = 'dark',
  className = '',
}) {
  const isDark = theme === 'dark';

  const safeTag =
    (hashtag || '').trim().startsWith('#') ? (hashtag || '').trim() : `#${(hashtag || '').trim()}`;

  // Build star icons (supports halves)
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  const Star = ({ filled = false }) => (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className={`h-4 w-4 ${filled ? (isDark ? 'text-yellow-400' : 'text-yellow-500') : (isDark ? 'text-gray-600' : 'text-gray-300')}`}
      fill="currentColor"
    >
      <path d="M10 15.27l-5.18 3.05 1.64-5.64L1.82 8.9l5.9-.51L10 3l2.28 5.39 5.9.51-4.64 3.78 1.64 5.64L10 15.27z" />
    </svg>
  );

  const HalfStar = () => (
    <div className="relative h-4 w-4">
      {/* left half (filled) */}
      <svg viewBox="0 0 20 20" className="absolute inset-0 h-4 w-4" fill="currentColor">
        <defs>
          <clipPath id="half">
            <rect x="0" y="0" width="10" height="20" />
          </clipPath>
        </defs>
        <path
          d="M10 15.27l-5.18 3.05 1.64-5.64L1.82 8.9l5.9-.51L10 3l2.28 5.39 5.9.51-4.64 3.78 1.64 5.64L10 15.27z"
          clipPath="url(#half)"
          className={isDark ? 'text-yellow-400' : 'text-yellow-500'}
        />
      </svg>
      {/* outline (unfilled right half) */}
      <svg viewBox="0 0 20 20" className="absolute inset-0 h-4 w-4" fill="currentColor">
        <path
          d="M10 15.27l-5.18 3.05 1.64-5.64L1.82 8.9l5.9-.51L10 3l2.28 5.39 5.9.51-4.64 3.78 1.64 5.64L10 15.27z"
          className={isDark ? 'text-gray-600' : 'text-gray-300'}
        />
      </svg>
    </div>
  );

  return (
    <div
      className={[
        'w-full rounded-2xl border p-4',
        isDark ? 'bg-[#0b0b0c] border-white/10 text-white' : 'bg-white border-black/10 text-gray-900',
        className,
      ].join(' ')}
      role="article"
    >
      {/* Row 1: avatar | name+tag | rating */}
      <div className="grid grid-cols-[auto,1fr,auto] items-center gap-3">
        {/* Col 1: avatar */}
        <img
          src={avatarSrc}
          alt=""
          className="h-12 w-12 rounded-full object-cover border border-black/10"
        />

        {/* Col 2: name + hashtag */}
        <div className="min-w-0">
          <div className="text-sm font-semibold truncate">{name}</div>
          <div className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {safeTag}
          </div>
        </div>

        {/* Col 3: rating (5 stars) */}
        <div className="flex items-center gap-[2px]" aria-label={`Rating: ${rating} out of 5`}>
          {Array.from({ length: fullStars }).map((_, i) => (
            <Star key={`full-${i}`} filled />
          ))}
          {hasHalf && <HalfStar key="half" />}
          {Array.from({ length: emptyStars }).map((_, i) => (
            <Star key={`empty-${i}`} filled={false} />
          ))}
        </div>
      </div>

      {/* Row 2: title + text */}
      <div className="mt-3">
        <div className="text-sm font-semibold">{reviewTitle}</div>
        <p className={`mt-1 text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
          {reviewText}
        </p>
      </div>
    </div>
  );
}
